// Trackeamento > Landing pages: páginas da empresa, criação a partir de modelos e contatos recebidos.
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Copy, ExternalLink, Eye, LayoutTemplate, MoreHorizontal, Pencil, Plus, Rocket, Trash2, Users, X } from 'lucide-react'
import { LANDING_TEMPLATES, type LandingPageSummaryDto, type LandingTemplateId } from '@impulso/shared/api/tracking'
import { landingTemplate, renderLandingPage } from '@impulso/shared/landing'
import { errorMessage } from '../../../lib/api'
import { useCan, useCompany } from '../../../lib/company'
import { Empty, ErrorState, Loading, PageHead, Tabs } from '../../../ui'
import { useConfirm } from '../../../ui/confirm'
import { useToast } from '../../../ui/toast'
import { useCreateLanding, useLandingAction, useLandingPages } from '../../../features/tracking/api'
import { LandingAddonCard } from '../../../features/tracking/components'
import LeadManager from '../../../features/tracking/LeadManager'
import DomainsTab from '../../../features/landing/DomainsTab'

const STATUS = { draft: { t: 'Rascunho', c: 'purple' }, published: { t: 'No ar', c: 'green' }, unpublished: { t: 'Fora do ar', c: '' } } as const

export const TEMPLATE_INFO: Record<LandingTemplateId, { name: string; description: string }> = {
  services: { name: 'Serviços', description: 'Para quem presta serviço: salão, clínica, oficina, reformas…' },
  leads: { name: 'Captura de contatos', description: 'Foco no formulário: orçamento, avaliação, agendamento.' },
  offer: { name: 'Oferta / produto', description: 'Promoção com contagem regressiva, preços e depoimentos.' },
  food: { name: 'Restaurante e delivery', description: 'Cardápio em fotos, pedido pelo WhatsApp e mapa.' },
  link_bio: { name: 'Link da bio', description: 'Página simples com seus contatos, ótima para o Instagram.' },
  blank: { name: 'Em branco', description: 'Comece do zero e monte do seu jeito.' },
}
export default function LandingPages() {
  const [params, setParams] = useSearchParams()
  const aba = params.get('aba')
  const tab = aba === 'contatos' || aba === 'dominio' ? aba : 'paginas'
  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Páginas" sub="Páginas feitas para anúncio: carregam rápido no celular, levam para o WhatsApp e já saem com o rastreamento instalado." />
      <Tabs value={tab} onChange={(v) => setParams(v === 'paginas' ? {} : { aba: v })} options={[{ value: 'paginas', label: 'Páginas' }, { value: 'contatos', label: 'Contatos recebidos' }, { value: 'dominio', label: 'Domínio próprio' }]} />
      {tab === 'paginas' ? <PagesTab /> : tab === 'contatos' ? <LeadManager /> : <DomainsTab />}
    </div>
  )
}

function PagesTab() {
  const company = useCompany()
  const canEdit = useCan('company.edit_drafts')
  const q = useLandingPages(company.id)
  const [creating, setCreating] = useState(false)
  if (q.isLoading) return <Loading lines={5} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  const { items, allowance } = q.data!
  return (
    <>
      {!allowance.active && <LandingAddonCard allowance={allowance} />}
      <div className="row between wrap" style={{ gap: 8 }}>
        <span className="small muted">{allowance.active ? `${allowance.published} de ${allowance.maxPublished} páginas publicadas na conta` : ''}</span>
        {canEdit && <button className="btn primary" onClick={() => setCreating(true)}><Plus size={16} /> Nova página</button>}
      </div>
      {items.length === 0 ? (
        <Empty title="Nenhuma página ainda">
          <p>Escolha um modelo pronto e troque os textos e fotos. Leva poucos minutos.</p>
          {canEdit && <button className="btn primary mt-12" onClick={() => setCreating(true)}><LayoutTemplate size={16} /> Criar minha primeira página</button>}
        </Empty>
      ) : (
        <div className="lp-card-list">{items.map((p) => <PageCard key={p.id} page={p} />)}</div>
      )}
      {creating && <CreateModal onClose={() => setCreating(false)} />}
    </>
  )
}

function PageCard({ page }: { page: LandingPageSummaryDto }) {
  const company = useCompany()
  const canEdit = useCan('company.edit_drafts')
  const canPublish = useCan('company.publish')
  const act = useLandingAction(company.id)
  const toast = useToast()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const [menu, setMenu] = useState(false)
  const st = STATUS[page.status]
  const run = async (action: 'publish' | 'unpublish' | 'duplicate' | 'delete', msg: string) => {
    setMenu(false)
    if (action === 'delete' && !(await confirm({ title: `Excluir a página "${page.title}"?`, message: 'Os contatos recebidos continuam salvos.', confirmLabel: 'Excluir', danger: true }))) return
    if (action === 'unpublish' && !(await confirm({ title: 'Tirar a página do ar?', message: 'Quem clicar no anúncio vai ver "página indisponível".', confirmLabel: 'Tirar do ar', danger: true }))) return
    act.mutate({ id: page.id, action }, { onSuccess: () => toast(msg), onError: (e) => toast(errorMessage(e)) })
  }
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(page.url); toast('Endereço copiado.') } catch { toast(page.url) }
  }
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="row between" style={{ gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.title}</h3>
          <div className="row wrap mt-8" style={{ gap: 6 }}>
            <span className={`badge ${st.c}`}>{st.t}</span>
            {page.hasUnpublishedChanges && <span className="badge amber">Alterações não publicadas</span>}
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <button className="icon-btn" aria-label="Mais ações" onClick={() => setMenu(!menu)}><MoreHorizontal size={17} /></button>
          {menu && (
            <div className="card" style={{ position: 'absolute', right: 0, top: 42, padding: 6, zIndex: 30, minWidth: 190, boxShadow: 'var(--shadow-lg)' }} onMouseLeave={() => setMenu(false)}>
              {canEdit && <button className="nav-item" style={{ width: '100%', border: 'none', background: 'none' }} onClick={() => run('duplicate', 'Página duplicada.')}><Copy size={15} /> Duplicar</button>}
              {canPublish && page.status === 'published' && <button className="nav-item" style={{ width: '100%', border: 'none', background: 'none' }} onClick={() => run('unpublish', 'Página fora do ar.')}><X size={15} /> Tirar do ar</button>}
              {canPublish && <button className="nav-item" style={{ width: '100%', border: 'none', background: 'none', color: 'var(--red)' }} onClick={() => run('delete', 'Página excluída.')}><Trash2 size={15} /> Excluir</button>}
            </div>
          )}
        </div>
      </div>
      <div className="row" style={{ gap: 6, minWidth: 0 }}>
        <span className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{page.url}</span>
        <button className="btn ghost sm" aria-label="Copiar endereço" onClick={() => void copyUrl()}><Copy size={14} /></button>
        {page.status === 'published' && <a className="btn ghost sm" aria-label="Abrir página" href={page.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /></a>}
      </div>
      <div className="row" style={{ gap: 18 }}>
        <span className="small row" style={{ gap: 6 }}><Eye size={15} color="var(--muted)" /> <b>{page.views30d}</b> visitas</span>
        <span className="small row" style={{ gap: 6 }}><Users size={15} color="var(--muted)" /> <b>{page.leads30d}</b> contatos</span>
        <span className="small muted" style={{ marginLeft: 'auto' }}>30 dias</span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn sm" style={{ flex: 1 }} onClick={() => navigate(`/app/${company.id}/trackeamento/landing-pages/${page.id}`)}><Pencil size={14} /> {canEdit ? 'Editar' : 'Ver'}</button>
        {canPublish && (page.status !== 'published' || page.hasUnpublishedChanges) && (
          <button className="btn primary sm" style={{ flex: 1 }} disabled={act.isPending} onClick={() => run('publish', 'Página publicada!')}><Rocket size={14} /> Publicar</button>
        )}
      </div>
    </div>
  )
}

function TemplateThumb({ id, name }: { id: LandingTemplateId; name: string }) {
  const html = useMemo(() => renderLandingPage(landingTemplate(id, { name, whatsapp: '11999999999', city: null, segment: null }), { assetUrl: () => null, editor: true }), [id, name])
  return <div className="lp-thumb"><iframe title={`Modelo ${TEMPLATE_INFO[id].name}`} srcDoc={html} sandbox="" loading="lazy" tabIndex={-1} /></div>
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const company = useCompany()
  const create = useCreateLanding(company.id)
  const navigate = useNavigate()
  const [template, setTemplate] = useState<LandingTemplateId>('services')
  const [title, setTitle] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    create.mutate({ title: title.trim() || TEMPLATE_INFO[template].name, template, whatsapp: whatsapp.trim() || undefined }, {
      onSuccess: (p) => navigate(`/app/${company.id}/trackeamento/landing-pages/${p.id}`),
    })
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby="new-lp" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-head"><h2 id="new-lp">Nova página</h2><button type="button" className="icon-btn" style={{ marginLeft: 'auto' }} aria-label="Fechar" onClick={onClose}><X size={17} /></button></div>
        <div className="modal-body">
          <div className="grid g-2" style={{ gap: 12 }}>
            <div className="field"><label htmlFor="lp-title">Nome da página</label><input id="lp-title" className="input" placeholder="Ex.: Promoção de Dia das Mães" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus /></div>
            <div className="field"><label htmlFor="lp-wa">WhatsApp de atendimento</label><input id="lp-wa" className="input" inputMode="tel" placeholder="(11) 99999-9999" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} /></div>
          </div>
          <div className="field">
            <label>Escolha um modelo</label>
            <div className="tpl-grid" role="radiogroup" aria-label="Modelo">
              {LANDING_TEMPLATES.map((id) => (
                <button key={id} type="button" role="radio" aria-checked={template === id} className={`tpl ${template === id ? 'on' : ''}`} onClick={() => setTemplate(id)}>
                  <TemplateThumb id={id} name={company.name} />
                  <div><div className="strong small">{TEMPLATE_INFO[id].name}</div><div className="small muted" style={{ fontSize: 12 }}>{TEMPLATE_INFO[id].description}</div></div>
                </button>
              ))}
            </div>
          </div>
          {create.error && <div className="form-error" role="alert">{errorMessage(create.error)}</div>}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" disabled={create.isPending}>{create.isPending ? 'Criando…' : 'Criar e abrir o editor'}</button>
        </div>
      </form>
    </div>
  )
}
